/**
 * BOSS Reports — which row fields are frozen at the left, and which scroll with the data.
 *
 * Every row field stays in `group_by`, always. That is what makes the rows what they are — one per
 * distinct combination of ALL row fields — and it is why pinning can never merge rows, re-sum a
 * measure, or turn a description into a "value". Pinning is purely a question of where the
 * datagrid DRAWS a row field: in the frozen block on the left, or as a scrolling column before the
 * measures. The drawing is done by lib/bossRowPinning; this module owns the state.
 *
 * The state is two ordered lists, persisted in the layout envelope beside `viewer` (never inside
 * it — Perspective's `save()` drops anything it does not know, so a marker in the viewer config
 * would vanish on the first user edit):
 *
 *   pinnedRowFields    the frozen block, left to right
 *   unpinnedRowFields  the scrolling row fields, left to right
 *
 * Pinning APPENDS to the pinned list — the field lands at the right of the frozen block, beside
 * the columns it is joining. Unpinning PREPENDS to the unpinned list — the field lands at the
 * left of the scrolling block, beside the block it just left. Neither touches `group_by`, so the
 * row set and the row order are unchanged by either gesture.
 *
 * Absence means "everything pinned, in Rows order" — which is what every layout saved before this
 * existed looks like, and what the legacy desktop grid drew — so nothing needs migrating.
 */
import type { ViewerConfig } from './bossLayoutOps'

export type BossRowPins = {
    /** The frozen block, left to right. */
    pinned: string[]
    /** The scrolling row fields, left to right, drawn before the measures. */
    unpinned: string[]
}

/** The two envelope keys, as the server validates and stores them. */
export const PINNED_KEY = 'pinnedRowFields'
export const UNPINNED_KEY = 'unpinnedRowFields'

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const strings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []

/** The Rows area, as names. */
export function rowFieldsOf(config: ViewerConfig): string[] {
    return strings(config.group_by)
}

/**
 * Reconciles a stored (or edited) pin state with the config it now applies to.
 *
 * Every name that is not in Rows is dropped — a field dragged out of Rows is neither pinned nor
 * unpinned, it is gone. Every row field named in neither list is appended to the PINNED block,
 * which is how a field dragged into Rows starts frozen, at the right, without the drag having to
 * know pins exist. Duplicates and a name in both lists resolve in favour of its first appearance.
 * The result always partitions `group_by` exactly.
 */
export function normaliseRowPins(
    config: ViewerConfig,
    pins: Partial<BossRowPins> | null | undefined,
): BossRowPins {
    const rows = rowFieldsOf(config)
    const inRows = new Set(rows)
    const seen = new Set<string>()
    const take = (list: unknown): string[] => {
        const out: string[] = []
        for (const name of strings(list)) {
            if (!inRows.has(name) || seen.has(name)) continue
            seen.add(name)
            out.push(name)
        }
        return out
    }
    const pinned = take(pins?.pinned)
    const unpinned = take(pins?.unpinned)
    for (const name of rows) {
        if (!seen.has(name)) pinned.push(name)
    }
    return { pinned, unpinned }
}

/** Reads the two lists off a stored layout envelope, in whatever shape it came back. */
export function rowPinsOfEnvelope(envelope: unknown): Partial<BossRowPins> | null {
    if (!isRecord(envelope)) return null
    const pinned = envelope[PINNED_KEY]
    const unpinned = envelope[UNPINNED_KEY]
    if (pinned === undefined && unpinned === undefined) return null
    return { pinned: strings(pinned), unpinned: strings(unpinned) }
}

export function sameRowPins(a: BossRowPins, b: BossRowPins): boolean {
    return a.pinned.length === b.pinned.length
        && a.unpinned.length === b.unpinned.length
        && a.pinned.every((name, i) => name === b.pinned[i])
        && a.unpinned.every((name, i) => name === b.unpinned[i])
}

/** True when the field is drawn in the frozen block. A field not in Rows is never pinned. */
export function isRowFieldPinned(pins: BossRowPins, name: string): boolean {
    return pins.pinned.includes(name)
}

/**
 * Pins a row field: out of the scrolling block and onto the RIGHT-HAND end of the frozen one.
 *
 * Returns the same object when the field is already pinned or is not a row field at all, so a
 * caller comparing identity can tell a real change from a click that changed nothing.
 */
export function pinRowField(pins: BossRowPins, name: string): BossRowPins {
    if (pins.pinned.includes(name) || !pins.unpinned.includes(name)) return pins
    return {
        pinned: [...pins.pinned, name],
        unpinned: pins.unpinned.filter(entry => entry !== name),
    }
}

/**
 * Unpins a row field: out of the frozen block and onto the LEFT-HAND end of the scrolling one, so it
 * sits beside the block it just left rather than jumping past the measures.
 *
 * The frozen block may empty out entirely — a report with nothing held in place is a legitimate
 * choice, and the grid draws it as ordinary scrolling columns.
 */
export function unpinRowField(pins: BossRowPins, name: string): BossRowPins {
    if (pins.unpinned.includes(name) || !pins.pinned.includes(name)) return pins
    return {
        pinned: pins.pinned.filter(entry => entry !== name),
        unpinned: [name, ...pins.unpinned],
    }
}
